import GsuLoader from '@/components/GsuLoader'
import { PageMetaInfo } from '@/compositions/MetaInfo'
import { FragPageMetaInfo } from '@/compositions/MetaInfo/query'
import type { Props as PageComposerProps } from '@/compositions/PageComposer'
import { FragComposerSections } from '@/compositions/PageComposer/query'
import PageLayout from '@/compositions/PageLayout'
import { ModalProvider } from '@/context/modal'
import { queryGsuApi } from '@/service/query'
import { Page } from '@/shared/api-generated-types'
import type { GetServerSideProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import { gql, useQuery } from 'urql'

// PageComposer dynamic import to prevent SSR hydration issues
const PageComposerWithLoader = dynamic<PageComposerProps>(() => import('@/compositions/PageComposer'), {
  ssr: false,
  loading: () => <GsuLoader consumeSpace />,
})

const QUERY_CATEGORY = gql`
  query Category($slug: String!, $page:Int) {
    ${FragComposerSections}
    ${FragPageMetaInfo}
    page: categoryPage(slug: $slug, page: $page) {
      title
      id
      metaInfo {
        ...PageMetaInfo
      }
      sections {
        ...CoreSections
        ...RecursiveSections
      }
    }
  }
`

const Category: NextPage<{ slug: string }> = (props) => {
  const [currentPage, setCurrentPage] = useState<number>(0)

  const [{ data, fetching }, executeQuery] = useQuery({
    query: QUERY_CATEGORY,
    variables: { slug: props.slug, page: currentPage },
    requestPolicy: 'cache-first',
    pause: !props?.slug,
  })

  const handleLoadMore = (_idx: number, page: number): void => {
    setCurrentPage(page)
    executeQuery({ requestPolicy: 'network-only' })
  }

  const pageData: Page = data?.page || {}

  return (
    <PageLayout disablePageContentBottomPadding contentClassName="pb-lg pb-xl-gte-lg pb-xxl-gte-xxl">
      <PageMetaInfo {...pageData.metaInfo} />
      <ModalProvider>
        <PageComposerWithLoader {...pageData} fetching={fetching} onLoadMoreByPageNumber={handleLoadMore} />
      </ModalProvider>
    </PageLayout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ locale = 'en-US', req, res, query: { slug } }) => {
  const [urqlState, statusCode] = await queryGsuApi(QUERY_CATEGORY, { slug }, req)

  if (!!statusCode) {
    console.log('Category page failed to retrieve details', statusCode)
    res.statusCode = statusCode
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ['badge', 'common', 'classes'])),
      slug,
      urqlState,
      disableAuthPrompt: true,
    },
  }
}

export default Category
